import { useCallback, useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, type DeviceStoresRow } from '../../db/schema';

/**
 * Magasin courant de l'appareil (réunion du 09/10/2026).
 *
 * Les magasins ouverts viennent du back-office (`GET /users/me/stores`, mis
 * en cache par le CacheWarmer — hors ligne compris). Le CHOIX, lui, n'existe
 * que si plusieurs magasins sont ouverts : il est mémorisé par appareil, sous
 * une clé propre à l'utilisateur de l'appareil (le superviseur de la tablette
 * en session PIN), et oublié dès qu'il ne fait plus partie des magasins
 * ouverts. Un seul magasin = aucun choix.
 */

export type StoreSummary = DeviceStoresRow['stores'][number];

const KEY_PREFIX = 'doorly:store:';
const CHANGE_EVENT = 'doorly:store-change';

function readChoice(deviceUserId: string): string | null {
  try {
    return localStorage.getItem(KEY_PREFIX + deviceUserId);
  } catch {
    return null;
  }
}

function writeChoice(deviceUserId: string, storeId: string | null): void {
  try {
    if (storeId) localStorage.setItem(KEY_PREFIX + deviceUserId, storeId);
    else localStorage.removeItem(KEY_PREFIX + deviceUserId);
  } catch {
    // Stockage indisponible (navigation privée…) : le choix ne survivra pas
    // au rechargement, le bandeau le redemandera. Rien de bloquant.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export interface DeviceStoreState {
  /** Cache pas encore lu (premier rendu) — ne rien afficher. */
  loading: boolean;
  /** Réseau de boutiques : bandeau et magasin sur les fiches. */
  retail: boolean;
  stores: StoreSummary[];
  /** Magasin courant : l'unique magasin, ou le choix mémorisé s'il est encore ouvert. */
  current: StoreSummary | null;
  /** Plusieurs magasins ouverts et aucun choisi : choix obligatoire avant de saisir. */
  needsChoice: boolean;
  /**
   * Aucun magasin affecté ALORS QUE le réseau en a (bandeau d'alerte, ne
   * bloque pas la saisie). Réseau qui n'a encore créé aucun magasin : faux —
   * il migre à son rythme, rien ne change pour le terrain d'ici là.
   */
  noStore: boolean;
  choose: (storeId: string) => void;
}

/** Résolution pure — même règle que le bandeau et le wizard. */
export function resolveDeviceStore(
  row: DeviceStoresRow | undefined,
  choice: string | null,
): Pick<DeviceStoreState, 'retail' | 'stores' | 'current' | 'needsChoice' | 'noStore'> {
  const retail = row?.retail === true;
  const stores = retail ? (row?.stores ?? []) : [];
  const current = stores.length === 1 ? stores[0]! : (stores.find((s) => s.id === choice) ?? null);
  return {
    retail,
    stores,
    current,
    needsChoice: retail && stores.length > 1 && current === null,
    noStore: retail && !!row && row.networkHasStores === true && stores.length === 0,
  };
}

export function useDeviceStore(): DeviceStoreState {
  const row = useLiveQuery(() => db.deviceStores.get('device'), [], null);
  const deviceUserId = row?.deviceUserId ?? null;
  const [choice, setChoice] = useState<string | null>(() =>
    deviceUserId ? readChoice(deviceUserId) : null,
  );

  useEffect(() => {
    if (!deviceUserId) return;
    const sync = () => setChoice(readChoice(deviceUserId));
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [deviceUserId]);

  const resolved = resolveDeviceStore(row ?? undefined, choice);

  // Choix périmé (magasin retiré par le back-office) : on l'oublie.
  useEffect(() => {
    if (!deviceUserId || !choice || !row) return;
    if (!resolved.stores.some((s) => s.id === choice)) writeChoice(deviceUserId, null);
  }, [deviceUserId, choice, row, resolved.stores]);

  const choose = useCallback(
    (storeId: string) => {
      if (deviceUserId) writeChoice(deviceUserId, storeId);
    },
    [deviceUserId],
  );

  return { loading: row === null, ...resolved, choose };
}
